import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getDatabase, ref, set, onValue, remove, update, get } from 'firebase/database';

// ─── Pokémon Roster ───────────────────────────────────────────────────────────
const POKEMON_ROSTER = [
  { id: 'pikachu',    name: 'Pikachu',    sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png',   color: '#FFD700', special: '⚡ Thunder!' },
  { id: 'charizard',  name: 'Charizard',  sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/6.png',    color: '#FF6B35', special: '🔥 Flamethrower!' },
  { id: 'gengar',     name: 'Gengar',     sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/94.png',   color: '#8B5CF6', special: '👻 Shadow Ball!' },
  { id: 'mewtwo',     name: 'Mewtwo',     sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/150.png',  color: '#A78BFA', special: '🔮 Psystrike!' },
  { id: 'snorlax',    name: 'Snorlax',    sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/143.png',  color: '#6B7280', special: '💤 Body Slam!' },
  { id: 'togepi',     name: 'Togepi',     sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/175.png',  color: '#FCD34D', special: '✨ Metronome!' },
  { id: 'lucario',    name: 'Lucario',    sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/448.png',  color: '#3B82F6', special: '🦴 Aura Sphere!' },
  { id: 'gardevoir',  name: 'Gardevoir',  sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/282.png',  color: '#EC4899', special: '💫 Moonblast!' },
  { id: 'blaziken',   name: 'Blaziken',   sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/257.png',  color: '#F97316', special: '🦵 Blaze Kick!' },
  { id: 'umbreon',    name: 'Umbreon',    sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/197.png',  color: '#1F2937', special: '🌑 Dark Pulse!' },
  { id: 'dragonite',  name: 'Dragonite',  sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/149.png',  color: '#F59E0B', special: '🐉 Hyper Beam!' },
  { id: 'eevee',      name: 'Eevee',      sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/133.png',  color: '#D97706', special: '🌀 Swift!' },
  { id: 'mew',        name: 'Mew',        sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/151.png',  color: '#F9A8D4', special: '🌸 Transform!' },
  { id: 'gyarados',   name: 'Gyarados',   sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/130.png',  color: '#1D4ED8', special: '🌊 Hydro Pump!' },
  { id: 'jigglypuff', name: 'Jigglypuff', sprite: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/39.png',   color: '#FCA5A5', special: '🎤 Sing!' },
];

// ─── Constants ────────────────────────────────────────────────────────────────
const ARENA_W       = 800;
const ARENA_H       = 420;
const GROUND_Y      = 340;
const PLATFORM_Y    = 220;
const PLAYER_SIZE   = 52;
const GRAVITY       = 0.6;
const JUMP_FORCE    = -14;
const MOVE_SPEED    = 4;
const ATTACK_RANGE  = 90;
const ATTACK_DMG    = 12;
const SPECIAL_DMG   = 28;
const KNOCKBACK     = 18;
const MAX_LIVES     = 3;
const TICK_MS       = 50; // 20fps physics

// ─── Platforms ────────────────────────────────────────────────────────────────
const PLATFORMS = [
  { x: 0,   y: GROUND_Y,   w: ARENA_W, h: 10, main: true },
  { x: 120, y: PLATFORM_Y, w: 160,     h: 10 },
  { x: 340, y: 180,        w: 120,     h: 10 },
  { x: 520, y: PLATFORM_Y, w: 160,     h: 10 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const randSpawnX = () => clamp(Math.random() * (ARENA_W - 100) + 50, 50, ARENA_W - 50);

const defaultPlayer = (uid, pokemonId, username, avatar) => ({
  uid, pokemonId, username, avatar,
  x: randSpawnX(), y: GROUND_Y - PLAYER_SIZE,
  vx: 0, vy: 0,
  onGround: true,
  lives: MAX_LIVES,
  hp: 100,
  facing: 1,
  attacking: false,
  special: false,
  stunned: false,
  stunTimer: 0,
  specialCharge: 0,
  ko: false,
  respawnTimer: 0,
  hitFlash: 0,
  lastUpdated: Date.now(),
});

// ─── Main Component ───────────────────────────────────────────────────────────
export default function BattleArena({ database, discordUser, isSpectator = false }) {
  const [phase, setPhase]           = useState('lobby');   // lobby | select | battle | gameover
  const [myPokemon, setMyPokemon]   = useState(null);
  const [players, setPlayers]       = useState({});
  const [winner, setWinner]         = useState(null);
  const [hitEffects, setHitEffects] = useState([]);
  const [specialText, setSpecialText] = useState(null);
  const [countdown, setCountdown]   = useState(null);
  const [lobbyPhase, setLobbyPhase] = useState('waiting'); // waiting | countdown
  const myStateRef  = useRef(null);
  const keysRef     = useRef({});
  const tickRef     = useRef(null);
  const myUid       = discordUser?.id;

  // ─── Firebase refs ─────────────────────────────────────────────────────────
  const arenaRef   = ref(database, 'arena');
  const playersRef = ref(database, 'arena/players');
  const phaseRef   = ref(database, 'arena/phase');
  const meRef      = myUid ? ref(database, `arena/players/${myUid}`) : null;

  // ─── Listen to Firebase ────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onValue(arenaRef, snap => {
      const data = snap.val() || {};
      const ps = data.players || {};
      setPlayers(ps);
      if (data.phase && data.phase !== phase) setPhase(data.phase);
      if (data.winner) setWinner(data.winner);
      if (data.countdown !== undefined) setCountdown(data.countdown);
    });
    return () => unsub();
  }, []);

  // ─── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (meRef) remove(meRef);
      clearInterval(tickRef.current);
    };
  }, []);

  // ─── Join lobby ────────────────────────────────────────────────────────────
  const joinLobby = useCallback(async (pokemonId) => {
    if (!myUid || !discordUser) return;
    const poke = POKEMON_ROSTER.find(p => p.id === pokemonId);
    const player = defaultPlayer(
      myUid, pokemonId,
      discordUser.username,
      discordUser.avatar ? `https://cdn.discordapp.com/avatars/${myUid}/${discordUser.avatar}.png` : null
    );
    myStateRef.current = { ...player };
    await set(meRef, player);
    setMyPokemon(poke);
    setPhase('lobby');
  }, [myUid, discordUser]);

  // ─── Leave arena ───────────────────────────────────────────────────────────
  const leaveArena = useCallback(async () => {
    if (meRef) await remove(meRef);
    clearInterval(tickRef.current);
    setPhase('lobby');
    setMyPokemon(null);
    myStateRef.current = null;
  }, []);

  // ─── Host starts the game ──────────────────────────────────────────────────
  const startGame = useCallback(async () => {
    // 3 second countdown
    for (let i = 3; i >= 0; i--) {
      await update(arenaRef, { countdown: i });
      await new Promise(r => setTimeout(r, 1000));
    }
    // Reset all players
    const snap = await get(playersRef);
    const ps = snap.val() || {};
    const updates = {};
    Object.keys(ps).forEach(uid => {
      updates[uid] = { ...ps[uid], ...defaultPlayer(uid, ps[uid].pokemonId, ps[uid].username, ps[uid].avatar) };
    });
    await set(playersRef, updates);
    await update(arenaRef, { phase: 'battle', countdown: null, winner: null });
  }, []);

  // ─── Physics tick (runs locally, writes my state to Firebase) ──────────────
  useEffect(() => {
    if (phase !== 'battle' || !myUid || isSpectator) return;
    clearInterval(tickRef.current);

    tickRef.current = setInterval(() => {
      const me = myStateRef.current;
      if (!me || me.ko) return;

      let { x, y, vx, vy, onGround, facing, stunned, stunTimer,
            attacking, special, specialCharge, hitFlash } = me;

      // Stun
      if (stunned) {
        stunTimer = Math.max(0, stunTimer - 1);
        if (stunTimer === 0) stunned = false;
        myStateRef.current = { ...me, stunned, stunTimer };
        update(meRef, { stunned, stunTimer, x, y });
        return;
      }

      // Input
      const left  = keysRef.current['left']  || keysRef.current['ArrowLeft']  || keysRef.current['a'];
      const right = keysRef.current['right'] || keysRef.current['ArrowRight'] || keysRef.current['d'];
      const jump  = keysRef.current['jump']  || keysRef.current['ArrowUp']    || keysRef.current['w'];
      const atk   = keysRef.current['attack'];
      const spec  = keysRef.current['special'];

      // Movement
      if (left)  { vx = -MOVE_SPEED; facing = -1; }
      else if (right) { vx = MOVE_SPEED; facing = 1; }
      else vx *= 0.7;

      // Jump
      if (jump && onGround) vy = JUMP_FORCE;

      // Gravity
      vy += GRAVITY;
      x += vx;
      y += vy;

      // Platform collisions
      onGround = false;
      for (const pl of PLATFORMS) {
        if (x + PLAYER_SIZE > pl.x && x < pl.x + pl.w) {
          if (vy >= 0 && y + PLAYER_SIZE >= pl.y && y + PLAYER_SIZE <= pl.y + 20) {
            y = pl.y - PLAYER_SIZE;
            vy = 0;
            onGround = true;
          }
        }
      }

      // Bounds
      x = clamp(x, 0, ARENA_W - PLAYER_SIZE);
      if (y > ARENA_H + 60) {
        // Fell off — lose a life
        const lives = Math.max(0, (me.lives || MAX_LIVES) - 1);
        const ko = lives <= 0;
        const respawnTimer = ko ? 0 : 120;
        myStateRef.current = { ...me, lives, ko, respawnTimer, x: randSpawnX(), y: GROUND_Y - PLAYER_SIZE, vx: 0, vy: 0 };
        update(meRef, myStateRef.current);
        return;
      }

      // Special charge
      specialCharge = Math.min(100, specialCharge + 0.4);

      // Attack
      if (atk && !attacking) {
        attacking = true;
        setTimeout(() => {
          if (myStateRef.current) {
            myStateRef.current.attacking = false;
            update(meRef, { attacking: false });
          }
        }, 300);
        // Find nearest player and hit
        doAttack(false);
      }

      // Special
      if (spec && specialCharge >= 100 && !special) {
        special = true;
        specialCharge = 0;
        setTimeout(() => {
          if (myStateRef.current) {
            myStateRef.current.special = false;
            update(meRef, { special: false });
          }
        }, 600);
        doAttack(true);
      }

      hitFlash = Math.max(0, (hitFlash || 0) - 1);

      const next = { ...me, x, y, vx, vy, onGround, facing, stunned, stunTimer,
                     attacking, special, specialCharge, hitFlash, lastUpdated: Date.now() };
      myStateRef.current = next;
      update(meRef, { x, y, vx, vy, onGround, facing, attacking, special, specialCharge, hitFlash, lastUpdated: Date.now() });
    }, TICK_MS);

    return () => clearInterval(tickRef.current);
  }, [phase, myUid, isSpectator]);

  // ─── Do attack (reads other players from Firebase state) ───────────────────
  const doAttack = useCallback((isSpecial) => {
    const me = myStateRef.current;
    if (!me) return;
    const ps = Object.values(players);
    let nearest = null, nearestDist = Infinity;
    for (const p of ps) {
      if (p.uid === myUid || p.ko) continue;
      const dist = Math.abs(p.x - me.x);
      if (dist < ATTACK_RANGE && dist < nearestDist) { nearest = p; nearestDist = dist; }
    }
    if (!nearest) return;

    const dmg    = isSpecial ? SPECIAL_DMG : ATTACK_DMG;
    const newHp  = Math.max(0, (nearest.hp || 100) - dmg);
    const kb     = (nearest.x > me.x ? 1 : -1) * KNOCKBACK * (isSpecial ? 2 : 1);
    const lives  = newHp <= 0 ? Math.max(0, (nearest.lives || MAX_LIVES) - 1) : nearest.lives;
    const ko     = lives <= 0 && newHp <= 0;

    update(ref(database, `arena/players/${nearest.uid}`), {
      hp: newHp <= 0 ? 100 : newHp,
      lives,
      ko,
      vx: kb,
      vy: -8,
      stunned: true,
      stunTimer: 8,
      hitFlash: 6,
      respawnTimer: ko ? 0 : newHp <= 0 ? 90 : 0,
    });

    // Show hit effect
    const poke = POKEMON_ROSTER.find(p => p.id === me.pokemonId);
    setHitEffects(prev => [...prev, {
      id: Date.now(),
      x: nearest.x,
      y: nearest.y,
      text: isSpecial ? (poke?.special || '💥 Special!') : dmg + ' dmg',
      isSpecial,
    }]);
    if (isSpecial) {
      setSpecialText(poke?.special || '💥 Special!');
      setTimeout(() => setSpecialText(null), 1500);
    }
    setTimeout(() => setHitEffects(prev => prev.filter(e => e.id !== Date.now())), 800);

    // Check win condition
    if (ko) {
      const remaining = Object.values(players).filter(p => p.uid !== nearest.uid && !p.ko);
      if (remaining.length === 0) {
        update(arenaRef, { phase: 'gameover', winner: discordUser?.username });
      }
    }
  }, [players, myUid, discordUser]);

  // ─── Keyboard controls ─────────────────────────────────────────────────────
  useEffect(() => {
    const down = e => {
      if (e.key === 'ArrowLeft'  || e.key === 'a') keysRef.current['left'] = true;
      if (e.key === 'ArrowRight' || e.key === 'd') keysRef.current['right'] = true;
      if (e.key === 'ArrowUp'    || e.key === 'w' || e.key === ' ') keysRef.current['jump'] = true;
      if (e.key === 'z' || e.key === 'Z') keysRef.current['attack'] = true;
      if (e.key === 'x' || e.key === 'X') keysRef.current['special'] = true;
    };
    const up = e => {
      if (e.key === 'ArrowLeft'  || e.key === 'a') keysRef.current['left'] = false;
      if (e.key === 'ArrowRight' || e.key === 'd') keysRef.current['right'] = false;
      if (e.key === 'ArrowUp'    || e.key === 'w' || e.key === ' ') keysRef.current['jump'] = false;
      if (e.key === 'z' || e.key === 'Z') keysRef.current['attack'] = false;
      if (e.key === 'x' || e.key === 'X') keysRef.current['special'] = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // Mobile button press/release
  const mobilePress   = key => keysRef.current[key] = true;
  const mobileRelease = key => keysRef.current[key] = false;

  const playerList = Object.values(players);
  const myPlayer   = players[myUid];
  const isHost     = playerList.length > 0 && playerList[0]?.uid === myUid;

  // ─── RENDER: Not logged in ─────────────────────────────────────────────────
  if (!discordUser) {
    return (
      <div
        className="relative flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center"
        style={{
          backgroundImage: 'url(/arena.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* dark fade overlay */}
        <div className="absolute inset-0" style={{background: 'rgba(15,23,42,0.92)'}} />
        <div className="relative z-10 text-5xl">⚔️</div>
        <div className="relative z-10">
          <h2 className="text-white font-bold text-xl" style={{textShadow:'0 2px 8px rgba(0,0,0,0.8)'}}>Battle Arena</h2>
          <p className="text-slate-400 text-sm mt-1" style={{textShadow:'0 1px 4px rgba(0,0,0,0.9)'}}>Go head to head with other viewers live on stream!</p>
        </div>
        <div className="relative z-10 flex flex-col gap-1 rounded-xl px-4 py-3 border border-slate-700/40 backdrop-blur-sm" style={{ background: 'rgba(15,23,42,0.45)' }}>
          <p className="text-slate-300 text-sm">You need to log in with Discord to join the battle!</p>
          <p className="text-slate-400 text-xs">Use the button in the top corner to log in.</p>
        </div>
      </div>
    );
  }

  // ─── RENDER: Character select ──────────────────────────────────────────────
  if (phase === 'select' || (!myPokemon && !isSpectator)) {
    return (
      <div className="px-3 py-4 max-w-2xl mx-auto">
        <style>{`
          @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
          .poke-card:hover img { animation: float 0.8s ease-in-out infinite; }
          .poke-card { transition: all 0.15s cubic-bezier(0.34,1.56,0.64,1); }
          .poke-card:hover { transform: scale(1.06); }
          .poke-card:active { transform: scale(0.95); }
        `}</style>
        <div className="text-center mb-5">
          <div className="text-3xl mb-2">⚔️</div>
          <h2 className="text-white font-bold text-lg">Choose your fighter</h2>
          <p className="text-slate-400 text-xs mt-1">Pick a Pokémon to battle with</p>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {POKEMON_ROSTER.map(poke => (
            <button
              key={poke.id}
              className="poke-card bg-slate-800/60 border border-slate-700/50 rounded-2xl p-3 flex flex-col items-center gap-1.5 active:scale-95"
              onClick={() => joinLobby(poke.id)}
            >
              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{background:`${poke.color}22`}}>
                <img src={poke.sprite} alt={poke.name} className="w-12 h-12 object-contain" style={{imageRendering:'pixelated'}} />
              </div>
              <span className="text-white font-bold text-[10px]">{poke.name}</span>
              <span className="text-slate-500 text-[8px] text-center leading-tight">{poke.special}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ─── RENDER: Game Over ─────────────────────────────────────────────────────
  if (phase === 'gameover') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-6">
        <style>{`@keyframes popIn{0%{transform:scale(0);opacity:0}70%{transform:scale(1.2)}100%{transform:scale(1);opacity:1}}.pop-in{animation:popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both}`}</style>
        <div className="pop-in text-6xl">🏆</div>
        <h2 className="pop-in text-white font-bold text-2xl" style={{animationDelay:'0.1s'}}>{winner} wins!</h2>
        <p className="pop-in text-slate-400 text-sm" style={{animationDelay:'0.2s'}}>What an absolute chaotic battle.</p>
        {isHost && (
          <button
            className="pop-in mt-2 px-6 py-3 rounded-2xl text-white font-bold text-sm active:scale-95 transition-all"
            style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)', animationDelay:'0.3s'}}
            onClick={startGame}
          >
            Play Again 🔥
          </button>
        )}
        {!isHost && <p className="text-slate-500 text-xs">Waiting for host to restart...</p>}
      </div>
    );
  }

  // ─── RENDER: Lobby ─────────────────────────────────────────────────────────
  if (phase === 'lobby') {
    return (
      <div className="px-3 py-4 max-w-2xl mx-auto">
        <style>{`
          @keyframes pulse-ring{0%{transform:scale(0.95);box-shadow:0 0 0 0 rgba(34,211,238,0.4)}70%{transform:scale(1);box-shadow:0 0 0 8px rgba(34,211,238,0)}100%{transform:scale(0.95);box-shadow:0 0 0 0 rgba(34,211,238,0)}}
          .pulse-ring{animation:pulse-ring 1.5s ease-in-out infinite}
        `}</style>

        {/* Lobby header */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-4 mb-3 text-center">
          <div className="text-3xl mb-2">⚔️</div>
          <h2 className="text-white font-bold text-lg">Battle Lobby</h2>
          {countdown !== null && countdown > 0 ? (
            <div className="mt-3">
              <div className="text-5xl font-black text-cyan-400">{countdown}</div>
              <p className="text-slate-400 text-xs mt-1">Get ready!</p>
            </div>
          ) : (
            <p className="text-slate-400 text-xs mt-1">{playerList.length}/20 players joined</p>
          )}
        </div>

        {/* Your character */}
        {myPokemon && myPlayer && (
          <div className="bg-slate-800/60 border border-cyan-500/30 rounded-2xl p-3 mb-3 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:`${myPokemon.color}33`}}>
              <img src={myPokemon.sprite} alt={myPokemon.name} className="w-10 h-10 object-contain" style={{imageRendering:'pixelated'}} />
            </div>
            <div>
              <p className="text-cyan-400 font-bold text-xs">Your fighter</p>
              <p className="text-white font-bold text-sm">{myPokemon.name}</p>
              <p className="text-slate-500 text-[10px]">{myPokemon.special}</p>
            </div>
            <button onClick={leaveArena} className="ml-auto text-slate-500 text-xs px-2 py-1 rounded-lg border border-slate-700/50 active:scale-95">Leave</button>
          </div>
        )}

        {/* Player list */}
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-3 mb-3">
          <p className="text-slate-400 text-xs font-semibold mb-2 uppercase tracking-wider">Players in lobby</p>
          {playerList.length === 0 && <p className="text-slate-600 text-xs text-center py-2">No one here yet...</p>}
          <div className="flex flex-wrap gap-2">
            {playerList.map(p => {
              const poke = POKEMON_ROSTER.find(pk => pk.id === p.pokemonId);
              return (
                <div key={p.uid} className="flex items-center gap-1.5 bg-slate-700/40 rounded-xl px-2 py-1.5">
                  {poke && <img src={poke.sprite} alt={poke.name} className="w-6 h-6 object-contain" style={{imageRendering:'pixelated'}} />}
                  <span className="text-white text-[10px] font-semibold">{p.username}</span>
                  {p.uid === myUid && <span className="text-cyan-400 text-[8px]">(you)</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Start button (host only) */}
        {isHost && playerList.length >= 1 && (
          <button
            className="pulse-ring w-full py-3.5 rounded-2xl text-white font-bold text-sm active:scale-95 transition-all"
            style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)'}}
            onClick={startGame}
          >
            ⚔️ Start Battle! ({playerList.length} players)
          </button>
        )}
        {isHost && playerList.length < 1 && (
          <p className="text-center text-slate-500 text-xs">Waiting for players to join...</p>
        )}
        {!isHost && (
          <p className="text-center text-slate-500 text-xs">Waiting for {playerList[0]?.username} to start...</p>
        )}
      </div>
    );
  }

  // ─── RENDER: Battle ────────────────────────────────────────────────────────
  const myPoke = myPlayer ? POKEMON_ROSTER.find(p => p.id === myPlayer.pokemonId) : null;

  return (
    <div className="flex flex-col" style={{userSelect:'none', WebkitUserSelect:'none'}}>
      <style>{`
        @keyframes hitFlash{0%,100%{filter:brightness(1)}50%{filter:brightness(3) saturate(0)}}
        @keyframes dmgFloat{0%{opacity:1;transform:translateY(0) scale(1)}100%{opacity:0;transform:translateY(-40px) scale(1.3)}}
        @keyframes specialBanner{0%{opacity:0;transform:scale(0.5)}20%{opacity:1;transform:scale(1.1)}80%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(0.9)}}
        .hit-anim{animation:hitFlash 0.15s ease}
        .dmg-float{animation:dmgFloat 0.8s ease forwards}
        .special-banner{animation:specialBanner 1.5s ease forwards}
        @keyframes koSpin{0%{transform:rotate(0) scale(1)}100%{transform:rotate(720deg) scale(0)}}
        .ko-spin{animation:koSpin 0.6s ease forwards}
      `}</style>

      {/* ── Arena ── */}
      <div
        className="relative w-full overflow-hidden bg-slate-900"
        style={{
          height: '420px',
          maxWidth: '100%',
          background: 'linear-gradient(180deg, #0c1445 0%, #1a1a2e 50%, #16213e 100%)',
          borderBottom: '1px solid rgba(34,211,238,0.2)',
        }}
      >
        {/* Stars bg */}
        {[...Array(30)].map((_, i) => (
          <div key={i} className="absolute rounded-full bg-white" style={{
            width: Math.random() * 2 + 1 + 'px', height: Math.random() * 2 + 1 + 'px',
            top: Math.random() * 70 + '%', left: Math.random() * 100 + '%',
            opacity: Math.random() * 0.5 + 0.2,
          }} />
        ))}

        {/* Platforms */}
        {PLATFORMS.map((pl, i) => (
          <div key={i} className="absolute rounded-full" style={{
            left: `${(pl.x / ARENA_W) * 100}%`,
            top: `${(pl.y / ARENA_H) * 100}%`,
            width: `${(pl.w / ARENA_W) * 100}%`,
            height: pl.main ? '6px' : '10px',
            background: pl.main
              ? 'linear-gradient(90deg,rgba(34,211,238,0.3),rgba(59,130,246,0.5),rgba(34,211,238,0.3))'
              : 'linear-gradient(90deg,rgba(34,211,238,0.5),rgba(99,102,241,0.5))',
            boxShadow: '0 0 12px rgba(34,211,238,0.3)',
          }} />
        ))}

        {/* Players */}
        {playerList.map(p => {
          if (p.ko) return null;
          const poke = POKEMON_ROSTER.find(pk => pk.id === p.pokemonId);
          const isMe = p.uid === myUid;
          const pct  = `${(p.x / ARENA_W) * 100}%`;
          const pctY = `${(p.y / ARENA_H) * 100}%`;
          return (
            <div
              key={p.uid}
              className={p.hitFlash > 0 ? 'hit-anim' : ''}
              style={{
                position: 'absolute',
                left: pct, top: pctY,
                width: `${(PLAYER_SIZE / ARENA_W) * 100}%`,
                transition: 'left 0.05s linear, top 0.05s linear',
                zIndex: 10,
              }}
            >
              {/* Name tag */}
              <div className="absolute w-full flex justify-center" style={{top:'-18px'}}>
                <span className="text-white font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap" style={{
                  fontSize:'0.5rem',
                  background: isMe ? 'rgba(34,211,238,0.8)' : 'rgba(0,0,0,0.7)',
                  border: isMe ? '1px solid #22d3ee' : '1px solid rgba(255,255,255,0.15)',
                }}>
                  {p.username}
                </span>
              </div>
              {/* HP bar */}
              <div className="absolute w-full" style={{top:'-8px', height:'3px', background:'rgba(0,0,0,0.5)', borderRadius:'2px'}}>
                <div style={{
                  height:'100%', borderRadius:'2px',
                  width:`${p.hp}%`,
                  background: p.hp > 50 ? '#22d3ee' : p.hp > 25 ? '#f59e0b' : '#ef4444',
                  transition:'width 0.2s',
                }} />
              </div>
              {/* Sprite */}
              <img
                src={poke?.sprite}
                alt={poke?.name}
                style={{
                  width:'100%', imageRendering:'pixelated',
                  transform: `scaleX(${p.facing || 1})`,
                  filter: p.attacking ? `drop-shadow(0 0 6px ${poke?.color || '#fff'})` : 'none',
                  opacity: p.stunned ? 0.6 : 1,
                }}
              />
              {/* Special glow */}
              {p.special && (
                <div className="absolute inset-0 rounded-full" style={{
                  background:`radial-gradient(circle, ${poke?.color}88 0%, transparent 70%)`,
                  animation:'pulse-ring 0.3s ease infinite',
                }} />
              )}
              {/* Life pips */}
              <div className="absolute w-full flex justify-center gap-0.5" style={{bottom:'-8px'}}>
                {[...Array(MAX_LIVES)].map((_, li) => (
                  <div key={li} className="rounded-full" style={{
                    width:'5px', height:'5px',
                    background: li < p.lives ? (poke?.color || '#22d3ee') : 'rgba(255,255,255,0.15)',
                  }} />
                ))}
              </div>
            </div>
          );
        })}

        {/* Hit effects */}
        {hitEffects.map(e => (
          <div key={e.id} className="dmg-float absolute pointer-events-none font-black" style={{
            left: `${(e.x / ARENA_W) * 100}%`,
            top: `${(e.y / ARENA_H) * 100}%`,
            fontSize: e.isSpecial ? '0.85rem' : '0.7rem',
            color: e.isSpecial ? '#f59e0b' : '#ef4444',
            textShadow: '0 0 8px currentColor',
            zIndex: 50,
            whiteSpace: 'nowrap',
          }}>
            {e.text}
          </div>
        ))}

        {/* Special banner */}
        {specialText && (
          <div className="special-banner absolute inset-0 flex items-center justify-center pointer-events-none" style={{zIndex:60}}>
            <div className="px-6 py-3 rounded-2xl font-black text-white text-lg" style={{
              background:'linear-gradient(135deg,rgba(34,211,238,0.9),rgba(59,130,246,0.9))',
              boxShadow:'0 0 40px rgba(34,211,238,0.5)',
            }}>
              {specialText}
            </div>
          </div>
        )}

        {/* Countdown overlay */}
        {countdown !== null && countdown > 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{zIndex:70,background:'rgba(0,0,0,0.5)'}}>
            <div className="font-black text-white" style={{fontSize:'6rem', textShadow:'0 0 30px #22d3ee'}}>
              {countdown}
            </div>
          </div>
        )}

        {/* Player KO'd overlay */}
        {myPlayer?.ko && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{zIndex:70, background:'rgba(0,0,0,0.6)'}}>
            <div className="text-red-400 font-black text-4xl">KO!</div>
            <div className="text-slate-400 text-sm mt-2">You've been eliminated</div>
          </div>
        )}
      </div>

      {/* ── HUD (lives + special charge) ── */}
      {myPlayer && myPoke && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700/50 bg-slate-900/80">
          <div className="flex items-center gap-2">
            <img src={myPoke.sprite} alt={myPoke.name} className="w-7 h-7 object-contain" style={{imageRendering:'pixelated'}} />
            <div>
              <div className="flex gap-1">
                {[...Array(MAX_LIVES)].map((_, i) => (
                  <div key={i} className="w-3 h-3 rounded-full" style={{
                    background: i < (myPlayer.lives || 0) ? myPoke.color : 'rgba(255,255,255,0.1)',
                    boxShadow: i < (myPlayer.lives || 0) ? `0 0 4px ${myPoke.color}` : 'none',
                  }} />
                ))}
              </div>
              <div className="text-white font-bold text-[10px] mt-0.5">HP: {Math.round(myPlayer.hp || 0)}</div>
            </div>
          </div>
          <div className="text-center">
            <div className="text-slate-400 text-[9px] uppercase tracking-wider mb-0.5">Special</div>
            <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${myPlayer.specialCharge || 0}%`,
                  background: (myPlayer.specialCharge || 0) >= 100
                    ? 'linear-gradient(90deg,#f59e0b,#ef4444)'
                    : 'linear-gradient(90deg,#22d3ee,#3b82f6)',
                  boxShadow: (myPlayer.specialCharge || 0) >= 100 ? '0 0 8px #f59e0b' : 'none',
                }}
              />
            </div>
            {(myPlayer.specialCharge || 0) >= 100 && (
              <div className="text-yellow-400 text-[9px] font-bold mt-0.5 animate-pulse">READY!</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-slate-400 text-[9px]">{playerList.filter(p => !p.ko).length} alive</div>
            <div className="text-slate-500 text-[8px]">/{playerList.length} total</div>
          </div>
        </div>
      )}

      {/* ── Mobile Controls ── */}
      {!isSpectator && myPlayer && !myPlayer.ko && (
        <div className="px-4 pt-3 pb-4 bg-slate-900/90">
          <div className="flex items-end justify-between gap-3">

            {/* D-pad */}
            <div className="flex items-center gap-2">
              <button
                className="w-14 h-14 rounded-2xl flex items-center justify-center active:scale-90 select-none"
                style={{background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', fontSize:'1.4rem'}}
                onPointerDown={() => mobilePress('left')}
                onPointerUp={() => mobileRelease('left')}
                onPointerLeave={() => mobileRelease('left')}
              >◀</button>
              <button
                className="w-14 h-14 rounded-2xl flex items-center justify-center active:scale-90 select-none"
                style={{background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', fontSize:'1.4rem'}}
                onPointerDown={() => mobilePress('right')}
                onPointerUp={() => mobileRelease('right')}
                onPointerLeave={() => mobileRelease('right')}
              >▶</button>
            </div>

            {/* Jump */}
            <button
              className="w-14 h-14 rounded-full flex items-center justify-center active:scale-90 select-none font-black text-white text-sm"
              style={{background:'linear-gradient(135deg,#22d3ee,#3b82f6)', boxShadow:'0 4px 12px rgba(34,211,238,0.4)'}}
              onPointerDown={() => mobilePress('jump')}
              onPointerUp={() => mobileRelease('jump')}
              onPointerLeave={() => mobileRelease('jump')}
            >JUMP</button>

            {/* Attack + Special */}
            <div className="flex items-center gap-2">
              <button
                className="w-14 h-14 rounded-2xl flex items-center justify-center active:scale-90 select-none font-black text-white text-xs"
                style={{background:'linear-gradient(135deg,#ef4444,#dc2626)', boxShadow:'0 4px 12px rgba(239,68,68,0.35)'}}
                onPointerDown={() => mobilePress('attack')}
                onPointerUp={() => mobileRelease('attack')}
                onPointerLeave={() => mobileRelease('attack')}
              >ATK</button>
              <button
                className="w-14 h-14 rounded-2xl flex items-center justify-center active:scale-90 select-none font-black text-xs"
                style={{
                  background: (myPlayer?.specialCharge || 0) >= 100
                    ? 'linear-gradient(135deg,#f59e0b,#ef4444)'
                    : 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: (myPlayer?.specialCharge || 0) >= 100 ? 'white' : 'rgba(255,255,255,0.3)',
                  boxShadow: (myPlayer?.specialCharge || 0) >= 100 ? '0 4px 16px rgba(245,158,11,0.5)' : 'none',
                }}
                onPointerDown={() => mobilePress('special')}
                onPointerUp={() => mobileRelease('special')}
                onPointerLeave={() => mobileRelease('special')}
              >✨ SP</button>
            </div>
          </div>
          <p className="text-center text-slate-600 text-[9px] mt-2">Keyboard: ← → move • ↑ jump • Z attack • X special</p>
        </div>
      )}

      {/* Spectator / KO'd scoreboard */}
      {(isSpectator || myPlayer?.ko) && (
        <div className="px-3 py-3 bg-slate-900/80">
          <p className="text-slate-400 text-xs font-semibold mb-2">Scoreboard</p>
          <div className="flex flex-wrap gap-2">
            {playerList.map(p => {
              const poke = POKEMON_ROSTER.find(pk => pk.id === p.pokemonId);
              return (
                <div key={p.uid} className="flex items-center gap-1.5 px-2 py-1 rounded-xl" style={{background: p.ko ? 'rgba(239,68,68,0.1)' : 'rgba(34,211,238,0.1)', border: `1px solid ${p.ko ? 'rgba(239,68,68,0.2)' : 'rgba(34,211,238,0.2)'}`}}>
                  {poke && <img src={poke.sprite} alt={poke.name} className="w-5 h-5 object-contain" style={{imageRendering:'pixelated', opacity: p.ko ? 0.4 : 1}} />}
                  <span className="text-white text-[10px] font-semibold" style={{opacity: p.ko ? 0.4 : 1}}>{p.username}</span>
                  <div className="flex gap-0.5">
                    {[...Array(MAX_LIVES)].map((_, i) => (
                      <div key={i} className="w-2 h-2 rounded-full" style={{background: i < p.lives ? (poke?.color || '#22d3ee') : 'rgba(255,255,255,0.1)'}} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
